import { useState } from 'react';

import _ from 'lodash';

function useReport(report, facilityId, format = 'csv') {
  const { initialFilters, reportQuery, fileName } = report;
  const [filterValues, setFilterValues] = useState(initialFilters);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(false);
  const [contactInfo, setContactInfo] = useState(false);
  const [errors, setErrors] = useState([]);

  const triggerDownload = (url, delay = 0) => {
    const clickLink = () => {
      let linkElement = document.createElement('a');
      linkElement.type = format === 'csv' ? 'text/csv' : 'application/pdf';
      linkElement.download = fileName;
      linkElement.href = url;
      linkElement.dispatchEvent(new MouseEvent('click'));
    }
    if (delay) {
      setTimeout(clickLink, delay);
    } else {
      clickLink();
    }
  };

  const doReport = (format, isSummary = false) => async (isLoadFileFromURL = false) => {
    let response = null;

    try {
      if (isSummary) {
        setIsDownloadingSummary(true);
      } else if (format === 'csv') {
        setIsDownloadingCSV(true);
      } else if (format === 'pdf') {
        setIsDownloadingPDF(true);
      }

      response = await reportQuery(facilityId, filterValues, format);

      if (response.ok) {
        response = isLoadFileFromURL ? await response.json() : await response.blob();
      } else {
        const statusError = 'HTTP status code ' + response.status + ': ' + response.statusText;
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/plain')) {
          const body = await response.text();
          setErrors([body]);
        } else if (contentType?.includes('json')) {
          const body = await response.json();
          if (body?.errors?.length) {
            setErrors(body.errors);
          }
        }
        throw new Error(statusError);
      }

      if (isLoadFileFromURL) {
        if (response.errors.length) {
          setErrors(response.errors);
        } else if (!response.data.totalRecords) {
          setErrors(['No records found']);
        } else if (response.data.presignedUrl) {
          triggerDownload(response.data.presignedUrl, 100);
        }
      } else {
        const url = window.URL;
        const objectUrl = url.createObjectURL(response);
        triggerDownload(objectUrl);
        setTimeout(url.revokeObjectURL(objectUrl));
      }
    } catch (error) {
      if (!isLoadFileFromURL) {
        alert(error.message);
      }
    } finally {
      setIsDownloadingCSV(false);
      setIsDownloadingPDF(false);
      setIsDownloadingSummary(false);
    }

    return response;
  };

  const downloadCSVReport = doReport('csv');
  const downloadCSVSummaryReport = doReport('csv', true);

  const downloadPDFReport = doReport('pdf');
  const downloadPDFSummaryReport = doReport('pdf', true);

  const inputOnChange = (field, checkboxValue) => {
    if (checkboxValue !== undefined) {
      const values = _.cloneDeep(filterValues);
      values[field] = checkboxValue;
      setFilterValues(values);
    } else {
      return value => {
        const values = _.cloneDeep(filterValues);
        values[field] = value;
        setFilterValues(values);
      }
    }
  }

  const inputOnDropDownChange = (field, value) => {
    const values = _.cloneDeep(filterValues);
    values[field] = value;
    setFilterValues(values);
  }

  const resetFilters = () => {
    setFilterValues(initialFilters);
    setErrors([]);
  }

  return {
    filterValues,
    setFilterValues,
    downloadCSVReport,
    downloadCSVSummaryReport,
    downloadPDFReport,
    downloadPDFSummaryReport,
    inputOnChange,
    resetFilters,
    isDownloadingCSV,
    isDownloadingPDF,
    isDownloadingSummary,
    inputOnDropDownChange,
    errors,
    setErrors,
    currentBalance,
    setCurrentBalance,
    subscriptionStatus,
    setSubscriptionStatus,
    contactInfo,
    setContactInfo,
  };
}

export default useReport;
